import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { Notes } from '@/reports/sections/NotesSection/Notes/Notes';
import { Button, Input, styled } from '@/theme/index';

interface ReportNoteFormProps {
  permalink: string;
}

interface RawReportNote {
  body: string;
}

export const FormContainer = styled('form')`
  width: 100%;
  height: 100%;
  display: flex;
`;

const NoteInput = styled(Input)(({ theme }) => ({
  width: '100%',
  marginRight: theme.spacing(1),
}));

const NoteButton = styled(Button)`
  flex: 0 0 auto;
`;

export function NoteForm({ permalink }: ReportNoteFormProps) {
  const { trackEvent } = useAppConfigContext();
  const noteInput = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (newNote: RawReportNote) => {
      return Notes.createNote(permalink, newNote);
    },

    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['reportNotes'] }),
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    trackEvent('add note', 'engagement click', 'notes section');
    const input = noteInput.current?.value;
    if (input) {
      const newNote = { body: input };
      mutation.mutate(newNote);
    }
    if (noteInput.current) noteInput.current.value = '';
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      <NoteInput
        type="text"
        placeholder="Add a note here..."
        inputRef={noteInput}
      />
      <NoteButton type="submit" variant="contained" color="success">
        Add a note
      </NoteButton>
    </FormContainer>
  );
}
